import { useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";

// Which look the app uses: the system's, or one the user chose in Settings.
// Kept in this browser, like the SIP trace switch, and not on the server.

const STORAGE_KEY = "themeMode";
const CHANGE_EVENT = "themeModeChange";

export const THEME_MODES = ["system", "light", "dark"];

export const getThemeMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return THEME_MODES.includes(stored) ? stored : "system";
  } catch (error) {
    return "system";
  }
};

export const setThemeMode = (mode) => {
  const next = THEME_MODES.includes(mode) ? mode : "system";
  try {
    if (next === "system") {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  } catch (error) {
    // Private browsing: the choice lasts until the page is closed.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  return next;
};

// The chosen mode, following changes made anywhere in the app or in another tab.
export const useThemeMode = () => {
  const [mode, setMode] = useState(getThemeMode);
  useEffect(() => {
    const update = () => setMode(getThemeMode());
    window.addEventListener(CHANGE_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(CHANGE_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);
  return mode;
};

// True when the app should be dark: chosen, or left to a dark system.
export const useDarkMode = () => {
  const systemDark = useMediaQuery("(prefers-color-scheme: dark)");
  const mode = useThemeMode();
  return mode === "dark" || (mode === "system" && systemDark);
};
